import { useEffect, useRef, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"

import wsClient from "src/wsClient"
import { playUiSound } from "src/utils/audio"
import ModalService from "src/modals"
import { selectLobby, startWebSocket, leaveRoom, selectChatMessages } from "src/store/lobby"
import { selectUserId } from "src/store/user"
import { selectGameConfig, selectGameModeRules, selectMapSizes, selectSurvivalConfig } from "src/store/common"
import { activeWorld, mapSizeOf, setActiveWorld } from "src/utils/world"
import { crownsOn, mapLabel, mapSizeOptionLabel } from "src/utils/mapSizes"
import {
  gameModeName,
  isSurvival,
  otherTeam,
  seatRulesFor,
  survivalOpeningBots,
  survivalWaveBots,
} from "src/utils/gameModes"
import { selectCanAffordGame, selectGameEntryCost, selectMyAvatar, selectUnlockedRaces } from "src/store/economy"
import { addMessage } from "src/store/messages"
import { raceNames, racesByTeam, defaultRaceByTeam, describeBonus, specialUnitFor, unitName } from "src/utils/races"

import { DEFAULT_AVATAR, seatIconFor, seatPictureFor } from "src/utils/cosmetics"
import { roundLengthLabel } from "src/utils/duration"
import { ROUND_LENGTH_OPTIONS } from "src/utils/rounds"

import { avatars, races, unitIcons } from "src/assets/images"
import BaseLayout from "src/layouts/BaseLayout"
import Canvas from "src/components/Canvas"
import Chat from "src/components/Chat"
import ClassChip from "src/components/ClassChip"
import LeagueFrame from "src/components/LeagueFrame"
import PlayerCardButton from "src/components/PlayerCardButton"

import "./Lobby.css"
import { usePointerContext } from "src/utils/input"

// How often a client in a room with an awaited seat asks the server to check on it.
export const CHECK_ARRIVALS_EVERY_MS = 10000

const REQUIRED_CELLS = 3

const Lobby = () => {
  const { t } = useTranslation()
  // The verb these hints use: a finger taps, a mouse clicks (see utils/input).
  const pointerContext = usePointerContext()
  const teamNames = { human: t("lobby.teamHumans"), zombie: t("lobby.teamZombies") }
  const navigate = useNavigate()
  const dispatch = useDispatch()
  let { number } = useParams()
  number = parseInt(number)

  const activeLobby = useSelector(selectLobby)
  /* The room's world (requirement 35, M10/M13): made active during the render, so the canvas and
     every reader under it draw this room's map. Picking is the same on every size — three cities,
     on land — and the first `crowns` picks, in pick order, are the crowns. */
  const mapSize = mapSizeOf(activeLobby.mapSize)
  setActiveWorld(mapSize)

  /* Walking into a room is heard (owner, 2026-10-25), once per room, by every door — create, join,
     an invite link. Not on top of the chime of a game found a moment ago: that already said it. */
  useEffect(() => {
    playUiSound("enter", { unlessWithinMs: 1500 })
  }, [number])
  const mapSizes = useSelector(selectMapSizes)
  const crowns = crownsOn(mapSizes, mapSize, REQUIRED_CELLS)
  const userId = useSelector(selectUserId)
  // Your own picture off the wallet: the shop updates it the moment you choose one, where the
  // room only learns it on its next broadcast.
  const myAvatar = useSelector(selectMyAvatar)
  const gameConfig = useSelector(selectGameConfig)
  // Survival's whole tuning table, published (SV6). The cadence this screen quotes is the cadence
  // the spawner keeps, because it is the same number read twice and never written twice.
  const survival = useSelector(selectSurvivalConfig)
  // What this mode seats (SV2), published by the server so the START button and
  // `Lobby.ready_to_play` count the same seats. Every mode is a row; nothing here branches on
  // which mode it is to do arithmetic.
  const publishedSeatRules = useSelector(selectGameModeRules)
  const unlockedRaces = useSelector(selectUnlockedRaces)
  const entryCost = useSelector(selectGameEntryCost)
  const canAffordGame = useSelector(selectCanAffordGame)
  const host = activeLobby.host
  const mode = activeLobby.mode
  const survivalMode = isSurvival(mode)
  const teamMembers = (team) => activeLobby.lobbyUsers.filter((user) => user.team === team)
  const missingMembers = (team) => activeLobby.membersPerTeam - teamMembers(team).length
  const userLobby = activeLobby.lobbyUsers.find((lobbyUser) => lobbyUser.user.id === userId)
  // A bot is ready the moment it sits, so only the people lock the settings (requirement 35 M15).
  const settingsLocked = activeLobby.lobbyUsers.some((lobbyUser) => lobbyUser.ready && !lobbyUser.user.isBot)
  // Read through `?.` below: a room this account is no longer in is on its way out of the store
  // (`startWebSocket` drops it), and the render in between must not throw on nobody's seat.
  const userTeam = userLobby?.team

  const isHost = host.id === userId

  const joinTeam = (team) => wsClient.send("lobby", { action: "join_team", team })
  const leaveTeam = (team) => wsClient.send("lobby", { action: "leave_team", team })
  const addBot = (team, difficulty) => wsClient.send("lobby", { action: "add_bot", team, difficulty })
  const removeBot = (botUserId) => wsClient.send("lobby", { action: "remove_bot", user_id: botUserId })
  const removeMember = (memberUserId) => wsClient.send("lobby", { action: "remove_member", user_id: memberUserId })
  const setBotRace = (botUserId, race) => wsClient.send("lobby", { action: "set_bot_race", user_id: botUserId, race })
  // Host only, survival only (SV3). The server answers with the whole lobby, so nothing is kept
  // here in step — the picker shows what came back.
  const setSurvivalDifficulty = (difficulty) =>
    wsClient.send("lobby", { action: "set_survival_difficulty", difficulty })

  // A seat starts with the free default class picked (Q21): a new player used to face eight cards
  // and a disabled READY with no hint that choosing was the missing step. The default is what the
  // "play raceless" button sends, so this is that button pressed for them; any other card still
  // replaces it with one click.
  //
  // Only once the socket is open: a seat can exist before it — the search queue seats a player
  // before their browser has opened the room (`lobby/matchmaking.py`), and a reload lands on a
  // seat too — and a send then was "Not connected" twice, plus the reconnect it started racing
  // the connection already opening ("already connected").
  const userRace = userLobby?.race
  const [socketOpen, setSocketOpen] = useState(false)
  useEffect(() => {
    if (socketOpen && userTeam && !userRace) {
      // Silent: nobody pressed anything, the side just taken (which made its own sound) needs a class.
      wsClient.send("lobby", { action: "change_race", race: defaultRaceByTeam[userTeam] }, { silent: true })
    }
  }, [socketOpen, userTeam, userRace])

  const handleReady = () => {
    wsClient.send("lobby", { action: userLobby.ready ? "unset_ready" : "set_ready" })
  }

  const handleLeaveLobby = () => {
    // Closing the socket is only being away (gamekit 0.8.0): the leave is the request. The socket
    // goes first, so this phone is not told about its own leave.
    const lobbyNumber = activeLobby.number
    wsClient.close("lobby")
    dispatch(leaveRoom({ lobbyNumber }))
    dispatch({ type: "lobby/reset" })
    navigate("/")
  }

  const handlePlay = () => wsClient.send("lobby", { action: "start_game" })

  // A class the player has not bought is not sent to the server at all — the shop is opened
  // instead. The server refuses it either way (`RaceNotUnlocked`), which is where the rule lives;
  // this is so the click does something useful rather than producing an error the player already
  // knew was coming.
  const onSelectRace = (race) => {
    if (!unlockedRaces.includes(race)) {
      ModalService.show("ShopModal", { initialTab: "classes" })
      return
    }
    wsClient.send("lobby", { action: "change_race", race })
  }

  // The backend toggles: an own cell is removed, a free one is added, and a fourth is refused. With
  // all cells placed, a click on a free cell therefore moves the oldest marker there — first the
  // removal, then the addition, on the same socket so they arrive in that order — instead of
  // handing the player "You have already chosen all your starting cells" for the obvious move.
  const onClickLocation = ({ col, row }) => {
    if (!userLobby.team || userLobby.ready) return
    const isOwnCell = userLobby.locations.some((l) => l.column === col && l.row === row)
    if (!isOwnCell && userLobby.locations.length >= REQUIRED_CELLS) {
      const oldest = userLobby.locations[0]
      wsClient.send("lobby", { action: "switch_location", column: oldest.column, row: oldest.row })
    }
    wsClient.send("lobby", { action: "switch_location", column: col, row })
  }

  // Sharing a lobby meant reading the number out loud and having the other player type it into the
  // join dialog. The URL already identifies the lobby, so hand it over whole.
  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      dispatch(addMessage({ text: t("lobby.inviteLinkCopied"), type: "success" }))
    } catch (error) {
      dispatch(addMessage({ text: t("lobby.inviteLinkFallback", { url: window.location.href }), type: "info" }))
    }
  }

  // Seats play, the rest watch: only seated members count toward "complete" and "ready", so a
  // teamless member (the host who seated two bots, say) starts the game as a spectator instead
  // of blocking it for ever.
  const seatedUsers = activeLobby.lobbyUsers.filter((lobbyUser) => lobbyUser.team)

  /* Survival puts every person on ONE side and gives the other to the waves (SV2). The side is not
     a setting anybody picks: the first person to sit down chooses it, so it is read off the seats
     rather than stored, and it goes back to "either" when the room empties — exactly what the
     server does with `join_team`, which refuses the far side once anybody is on one. */
  const peopleSide = survivalMode ? seatedUsers[0]?.team ?? null : null
  const enemySide = survivalMode ? otherTeam(peopleSide) : null
  const seatRules = seatRulesFor(mode, publishedSeatRules, {
    membersPerTeam: activeLobby.membersPerTeam,
    survival,
  })
  /* One rule for every mode: every side that holds seats has its `min_seats_per_side` people on
     it, and — where the room has one side — all of them are on it. "Both columns full" was a
     classic lobby's answer and one survival can never give, since the enemy side holds no seats;
     since A1 (2026-09-20) it is not a classic lobby's answer either, because 1v2 is a game. With
     no rules in hand (a server that publishes no table and a lobby with no seat count) nothing is
     complete, rather than a floor being guessed.

     The same sentence `Lobby.ready_to_play` says, read off the same published row: the button
     greys itself for as long as it thinks the server will refuse. */
  const seatedSides = [...new Set(seatedUsers.map((seat) => seat.team))]
  const neededSides = seatRules && seatRules.sides > 1 ? ["human", "zombie"] : seatedSides
  const lobbyComplete =
    Boolean(seatRules) &&
    seatedSides.length > 0 &&
    seatedSides.length <= seatRules.sides &&
    neededSides.every(
      (side) => seatedUsers.filter((seat) => seat.team === side).length >= seatRules.min_seats_per_side
    )
  const allUsersReady = lobbyComplete && seatedUsers.every((user) => user.ready)
  /* Two seats on a side wearing the same colour is a game nobody can read, so it is caught here
     and not on round four. The server refuses the start on the same fact (`Lobby.colour_clashes`);
     this is what greys the button and names who has to move, which is the difference between a
     rule and a wall. */
  const colourClashes = ["human", "zombie"].filter((team) => {
    const chosen = seatedUsers.filter((seat) => seat.team === team).map((seat) => seat.unitIcon)
    return chosen.filter(Boolean).length !== new Set(chosen.filter(Boolean)).size
  })
  const userCanStartGame = activeLobby.host.id === userId && allUsersReady && colourClashes.length === 0
  const willSpectate = lobbyComplete && !userTeam
  // Readying up charges nothing yet, but the server refuses it without the price of a seat — the
  // fee is collected when the host starts, and a player who cannot pay stops the whole game. So
  // the button says so here, while there is still something they can do about it.
  const userCanClickReady =
    userLobby?.team && userLobby.race && userLobby.locations.length === REQUIRED_CELLS && canAffordGame

  // A seat the search queue gave is taken back when its player does not arrive
  // (`lobby/matchmaking.py`, ARRIVAL_SECONDS). The server runs nothing on a clock, so while a seat
  // here is awaited this client asks for the check; the room comes back only if somebody went.
  const awaitedSeats = activeLobby.awaiting?.length ?? 0
  useEffect(() => {
    if (!socketOpen || !awaitedSeats) return undefined
    const timer = setInterval(() => wsClient.send("lobby", { action: "check_arrivals" }), CHECK_ARRIVALS_EVERY_MS)
    return () => clearInterval(timer)
  }, [socketOpen, awaitedSeats])

  const isInitialized = useRef(false)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true
      // A fulfilled thunk carries no `error`; a socket that never opened does.
      Promise.resolve(dispatch(startWebSocket({ lobbyNumber: number }))).then((result) => {
        if (result && !result.error) setSocketOpen(true)
      })
    }
  }, [dispatch, number])

  /* How tall the bar actually is, published for the shell underneath it (owner, 2026-09-09: "in
     lobby, top buttons overlay a little bit the map").

     The bar is fixed and the shell is fixed under it at `--lobby-bar-h`, so the two agree about
     one number — and the number was written down. On the phone the bar wraps to four rows (name,
     meta, round length, buttons) and 104px was measured against one language on one width: at
     360px the Spanish button row ends 6px past it, and at 390px it clears by exactly one pixel.
     Six pixels of LEAVE over whatever is under it, and the shell scrolls, so what is under it is
     the roster and then the map.

     Measured rather than re-estimated: a taller word in a third language, or a host with a long
     name, moves it again. This publishes the height and never sets it — `--lobby-bar-h` remains
     the *floor* the bar keeps, and `--lobby-bar-real-h` is what the shell starts below, so the
     observed box's size can never depend on what the observation writes. */
  const barRef = useRef(null)
  useEffect(() => {
    const bar = barRef.current
    if (!bar || typeof ResizeObserver === "undefined") return undefined

    const root = document.documentElement
    const publish = () => root.style.setProperty("--lobby-bar-real-h", `${Math.ceil(bar.getBoundingClientRect().height)}px`)
    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(bar)
    return () => {
      observer.disconnect()
      root.style.removeProperty("--lobby-bar-real-h")
    }
  })

  // Convert lobbyUser location to elements for the canvas based on the team
  const elements = {}
  activeLobby.lobbyUsers.forEach((ul) => {
    const isYou = ul.user.id === userId
    ul.locations.forEach((location, pick) => {
      elements[`${location.column}-${location.row}`] = [
        // The seat's chosen look, not the account's (owner's report, 2026-09-04): picking a colour
        // has to change the thing it is a picture of, and this map is the only preview of it
        // anybody gets before the game starts.
        { type: ul.team, skin: seatIconFor(ul) },
        // The first `crowns` picks are the crowns (requirement 35, M3/M10): on L all three, on M
        // two, on S one. The rest are starting cities like any other, with no badge.
        ...(pick < crowns ? [{ type: "crown", held: true }] : []),
        // Ring the player's own cells so they can tell theirs from an ally's, and see the whole
        // cell that a click on it will remove.
        ...(isYou ? [{ type: "outline", color: ul.team === "zombie" ? "#e2564b" : "#5ce07b" }] : []),
      ]
    })
  })

  const chatMessages = useSelector(selectChatMessages)

  // What each player still owes the lobby, in the order the eye reads the slot: side, then race,
  // then cells. One line, so a row never has to grow to explain itself.
  const slotStatus = (lobbyUser) => {
    if (!lobbyUser.race) return { text: t("lobby.choosingRace"), tone: "pending" }
    // A bot picks its cities last (owner's U1, 2026-09-07): the server places them once every
    // person is ready and takes them back when anybody un-readies, so an empty seat here is the
    // bot waiting on the people, not a bot that forgot.
    if (lobbyUser.user.isBot && lobbyUser.locations.length === 0) {
      return { text: t("lobby.botPlacesLast").toLowerCase(), tone: "pending", showClass: true }
    }
    const cells = t("lobby.cellsCount", { placed: lobbyUser.locations.length, total: REQUIRED_CELLS })
    const tone = lobbyUser.locations.length === REQUIRED_CELLS ? "done" : "pending"
    // The class is the chip beside this text (`ClassChip`), so the line says only the cells.
    return { text: cells.toLowerCase(), tone, showClass: true }
  }

  const topBar = (
    <div className="lobby-bar" ref={barRef}>
      <div className="lobby-bar-title">
        <h1>{t("lobby.title", { number })}</h1>
        <p className="lobby-bar-meta">
          {/* "1v1" is its own name in every language; "survival" is a word (SV1). */}
          {gameModeName(mode)} ·{" "}
          {isHost ? null : (
            <>
              {mapLabel(mapSize)} · {activeLobby.ranked ? t("lobbyModals.ranked") : t("lobbyModals.unranked")} ·{" "}
            </>
          )}
          {t("lobby.host")}{" "}
          <b>{host.username}</b> · {t("lobby.mapSize.citiesAndCrowns", { cities: REQUIRED_CELLS, count: crowns })}
        </p>
        {/* The round's clock: everybody reads it, the host sets it (2026-08-21). */}
        <p className="lobby-bar-meta lobby-bar-round">
          ⏱ {t("lobby.roundLength")}{" "}
          {isHost ? (
            <select
              className="form-input lobby-round-select"
              disabled={settingsLocked}
              title={settingsLocked ? t("lobby.settingsLocked") : undefined}
              value={activeLobby.roundSeconds ?? 60}
              onChange={(event) =>
                wsClient.send("lobby", { action: "set_round_seconds", seconds: Number(event.target.value) })
              }
            >
              {ROUND_LENGTH_OPTIONS.map((seconds) => (
                <option key={seconds} value={seconds}>
                  {roundLengthLabel(seconds, t)}
                </option>
              ))}
            </select>
          ) : (
            <b>{roundLengthLabel(activeLobby.roundSeconds ?? 60, t)}</b>
          )}
          {/* The map and the stake are the host's too, and like the clock they stay put once a
              person is ready (requirement 35 M15). The server refuses it the same way. */}
          {isHost && mapSizes.length > 0 && (
            <select
              className="form-input lobby-round-select"
              aria-label={t("lobby.mapSize.label")}
              disabled={settingsLocked}
              title={settingsLocked ? t("lobby.settingsLocked") : undefined}
              value={mapSize}
              onChange={(event) => wsClient.send("lobby", { action: "set_map_size", map_size: event.target.value })}
            >
              {mapSizes.map((row) => (
                <option key={row.size} value={row.size}>
                  {mapSizeOptionLabel(row)}
                </option>
              ))}
            </select>
          )}
          {isHost && (
            <select
              className="form-input lobby-round-select"
              aria-label={t("lobbyModals.rankedLabel")}
              disabled={settingsLocked}
              title={settingsLocked ? t("lobby.settingsLocked") : undefined}
              value={activeLobby.ranked ? "ranked" : "unranked"}
              onChange={(event) =>
                wsClient.send("lobby", { action: "set_ranked", ranked: event.target.value === "ranked" })
              }
            >
              <option value="ranked">{t("lobbyModals.ranked")}</option>
              <option value="unranked">{t("lobbyModals.unranked")}</option>
            </select>
          )}
        </p>
      </div>
      <div className="lobby-bar-actions">
        {/* The lobby's top bar replaces BaseLayout's floating header, and with it lost the way
            into Settings (Q20) — the one place volume, language and the profile live. */}
        <button
          className="quiet"
          onClick={() => ModalService.show("SettingsModal")}
          title={t("game.topBar.settings")}
        >
          ⚙ {t("game.topBar.settings")}
        </button>
        <button className="quiet" onClick={copyInviteLink}>
          {t("lobby.inviteLink")}
        </button>
        <button className="cancel" onClick={handleLeaveLobby}>
          {t("lobby.leave")}
        </button>
      </div>
    </div>
  )

  const teamBlock = (team) => {
    const members = teamMembers(team)
    return (
      <div className="team-block" key={team}>
        <div className="team-head">
          <span className={`team-dot team-dot-${team}`} />
          <span className="section-label">{teamNames[team]}</span>
          <span className="team-rule" />
          <span className="team-count">
            {members.length}/{activeLobby.membersPerTeam}
          </span>
        </div>

        {members.map((member) => {
          const status = slotStatus(member)
          const isYou = member.user.id === userId
          const picture = seatPictureFor(member, isYou ? myAvatar : null)
          return (
            <div className={`lobby-slot${isYou ? " you" : ""}`} key={member.user.id}>
              {/* **The person** (owner, 2026-10-09): the player's own picture — what the ranking
                  and the header show — in the frame of their league; a bot is in no league and wears a black one. */}
              <PlayerCardButton user={member.user} picture={picture} className="slot-card-button">
                <LeagueFrame league={member.user.league} bot={Boolean(member.user.isBot)} className="slot-league-frame">
                  <span className="slot-avatar slot-avatar-picture">
                    <img src={avatars[picture] || avatars[DEFAULT_AVATAR]} alt={member.user.username} />
                  </span>
                </LeagueFrame>
              </PlayerCardButton>
              <div className="slot-body">
                <p className="slot-name">
                  <span className="slot-name-text">{member.user.username}</span>
                  {member.user.isBot ? (
                    <span className="slot-bot-tag">{t(`lobby.botDifficulty_${member.botDifficulty || "easy"}`)}</span>
                  ) : (
                    <span className="slot-points">{t("lobby.points", { points: member.user.rankingPoints })}</span>
                  )}
                </p>
                {/* The class as the game's roster draws it (owner, 2026-10-09), then the cells. */}
                <p className={`slot-status slot-status-${status.tone}`}>
                  {status.showClass ? <ClassChip race={member.race} team={team} className="slot-class" /> : null}
                  <span className="slot-status-text">{status.text}</span>
                </p>
                {/* Bots play any class free of charge — the host is picking an opponent, not
                    unlocking content — so this lists the team's whole roster. */}
                {member.user.isBot && isHost ? (
                  <select
                    className="form-input slot-add-bot slot-bot-race"
                    value={member.race || defaultRaceByTeam[team]}
                    onChange={(event) => setBotRace(member.user.id, event.target.value)}
                  >
                    {[defaultRaceByTeam[team], ...racesByTeam[team]].map((race) => (
                      <option key={race} value={race}>
                        {raceNames[race] || race}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
              {member.connected === false ? <span className="slot-away">{t("lobby.away")}</span> : null}
              {member.ready ? <span className="slot-ready">{t("lobby.ready")}</span> : null}
              {/* The map icon this seat plays in, at the right of the row beside the ✕, nearly as
                  big as the picture (owner, 2026-10-09), because the lobby is where it is chosen:
                  on your own seat, while you can still change it, it is the door to the picker. */}
              {isYou && !member.ready ? (
                <button
                  type="button"
                  className="slot-look slot-look-button"
                  onClick={() => ModalService.show("LookPickerModal")}
                  title={t("lobby.changeLook")}
                  aria-label={t("lobby.changeLook")}
                >
                  <img src={unitIcons[seatIconFor(member)]} alt="" />
                  <span className="slot-avatar-edit" aria-hidden="true">✎</span>
                </button>
              ) : (
                <span className="slot-look" title={t("lobby.mapIcon")}>
                  <img src={unitIcons[seatIconFor(member)]} alt="" />
                </span>
              )}
              {isYou && !member.ready ? (
                <button className="slot-leave" onClick={() => leaveTeam(team)} title={t("lobby.leaveTeam", { team: teamNames[team] })}>
                  ✕
                </button>
              ) : null}
              {member.user.isBot && isHost ? (
                <button className="slot-leave" onClick={() => removeBot(member.user.id)} title={t("lobby.removeBot")}>
                  ✕
                </button>
              ) : null}
              {!member.user.isBot && isHost && !isYou ? (
                <button className="slot-leave" onClick={() => removeMember(member.user.id)} title={t("lobby.removeMember")}>
                  ✕
                </button>
              ) : null}
            </div>
          )
        })}

        {/* Before anybody sits in a survival room, either side is a door rather than a roster of
            four: one JOIN each and the hint above them, because eight empty rows for a game that
            will only ever seat one side of them is a screen that has to be read twice. */}
        {Array.from({ length: survivalMode && !peopleSide ? 1 : missingMembers(team) }).map((_, index) => (
          <div className="lobby-slot empty" key={`empty-${index}`}>
            <span className="slot-avatar slot-avatar-empty">+</span>
            <div className="slot-body">
              <p className="slot-empty-label">{t("lobby.emptySlot")}</p>
            </div>
            {/* A seated player cannot join here without leaving their slot first (the ✕ on it),
                so for them this button is permanently dead — clutter, not affordance. */}
            {!userTeam ? (
              <button className="quiet slot-join" onClick={() => joinTeam(team)}>
                {t("lobby.join")}
              </button>
            ) : null}
            {/* A select that acts as a button: picking a difficulty seats the bot, and the value
                stays on the placeholder so the same control can fill the next slot too. Not in
                survival, where nobody seats bots (SV4): the enemy column is where its difficulty
                is chosen, and the server refuses `add_bot` outright (`SurvivalSeatsPeopleOnly`). */}
            {isHost && !survivalMode ? (
              <select
                className="form-input slot-add-bot"
                value=""
                onChange={(event) => event.target.value && addBot(team, event.target.value)}
              >
                <option value="">{t("lobby.addBot")}</option>
                {["easy", "hard", "impossible"].map((difficulty) => (
                  <option key={difficulty} value={difficulty}>
                    {t(`lobby.botDifficulty_${difficulty}`)}
                  </option>
                ))}
              </select>
            ) : null}
          </div>
        ))}
      </div>
    )
  }

  /* The side nobody sits on, in a survival room. It is not an empty roster — it is the enemy, and
     the two things a player wants off this column before pressing READY are how good the waves are
     and how fast they arrive. The difficulty is the host's (SV3) and everybody else reads it; the
     cadence is quoted from `gameConfig.survival`, never from a constant of this bundle's own, for
     the same reason every price on a button is. */
  const enemyNames = { zombie: t("lobby.survivalEnemyHorde"), human: t("lobby.survivalEnemyHumanity") }
  const botDifficulties = gameConfig?.bot_difficulties || []
  const enemyBlock = (team) => {
    const difficulty = activeLobby.survivalDifficulty
    const opening = survivalOpeningBots(seatedUsers.length, survival)
    const perWave = survivalWaveBots(seatedUsers.length, survival)
    const everyRounds = survival?.WAVE_EVERY_ROUNDS
    return (
      <div className="team-block enemy-block" key={team}>
        <div className="team-head">
          <span className={`team-dot team-dot-${team}`} />
          <span className="section-label">{enemyNames[team]}</span>
          <span className="team-rule" />
          <span className="team-count">∞</span>
        </div>
        <div className="enemy-card">
          <p className="enemy-lead">{t("lobby.survivalEnemyLead")}</p>
          <p className="enemy-difficulty">
            <span className="enemy-difficulty-label">{t("lobby.survivalDifficulty")}</span>{" "}
            {difficulty && isHost ? (
              <select
                className="form-input lobby-round-select"
                aria-label={t("lobby.survivalDifficulty")}
                value={difficulty}
                onChange={(event) => setSurvivalDifficulty(event.target.value)}
              >
                {botDifficulties.map((value) => (
                  <option key={value} value={value}>
                    {t(`lobby.botDifficulty_${value}`)}
                  </option>
                ))}
              </select>
            ) : (
              <b>{difficulty ? t(`lobby.botDifficulty_${difficulty}`) : "—"}</b>
            )}
          </p>
          {opening && perWave && everyRounds ? (
            <p className="enemy-cadence">
              {t("lobby.survivalCadence", {
                opening: t("lobby.survivalEnemiesCount", { count: opening }),
                wave: perWave,
                rounds: t("lobby.survivalRoundsCount", { count: everyRounds }),
              })}
            </p>
          ) : null}
        </div>
      </div>
    )
  }

  // The one line that answers "why can't we start yet?", built from the same facts the START
  // button is disabled on, so the two can never disagree.
  const blockers = []
  activeLobby.lobbyUsers
    .filter((lobbyUser) => lobbyUser.team && !lobbyUser.ready)
    .forEach((lobbyUser) => blockers.push(lobbyUser.user.username))
  // Same list the button is disabled on, so the two cannot disagree.
  const clashWarnings = colourClashes.map((team) => t("lobby.colourClash", { team: teamNames[team] }))
  /* "2 empty zombie slots" is an answer only a two-sided lobby can give. A one-sided room is short
     of people, not of a side — and it is short only up to `min_seats`, since anything above that
     is a seat somebody may still take rather than one the start is waiting for. */
  /* With no row to read, a survival room still knows it has one side — it simply cannot say how
     many people it is short, so it says nothing rather than counting empty seats on a column
     nobody may sit in. */
  const oneSidedRoom = seatRules ? seatRules.sides === 1 : survivalMode
  const emptySlots = oneSidedRoom
    ? seatRules && seatedUsers.length < seatRules.min_seats
      ? [t("lobby.survivalNeedsPlayers", { count: seatRules.min_seats - seatedUsers.length })]
      : []
    : ["human", "zombie"]
        .map((team) => ({ team, missing: missingMembers(team) }))
        .filter(({ missing }) => missing > 0)
        .map(({ team, missing }) => t(`lobby.emptySlots_${team}`, { count: missing }))

  const raceBonuses = gameConfig?.rules_config?.RACE_BONUSES || {}
  const unitsPerRace = gameConfig?.valid_units_per_race || {}
  /* A room this account is no longer in: the socket handler is dropping it from the store
     (`startWebSocket`, on a `lobby_refreshed` without this seat — another tab of the same account
     gave it up), and the router takes the player home on the next render. Drawing it in between
     dereferenced nobody's seat and took the whole app down: a black screen a few seconds into
     the lobby, cured by a reload (owner, 2026-09-24). After every hook, so the order holds. */
  if (!userLobby) return null

  const defaultRace = userTeam ? defaultRaceByTeam[userTeam] : null

  const setup = (
    <div className="lobby-setup">
      <div className="setup-head">
        <span className="section-label">{t("lobby.yourRace")}</span>
        <span className="team-rule" />
        {defaultRace ? (
          <button className="setup-raceless" onClick={() => onSelectRace(defaultRace)} disabled={userLobby.ready}>
            {t("lobby.playRaceless", { race: raceNames[defaultRace] })}
          </button>
        ) : null}
      </div>

      {userTeam ? (
        <div className="race-cards">
          {racesByTeam[userTeam].map((race) => {
            const bonuses = Object.entries(raceBonuses[race] || {})
            const specialUnit = specialUnitFor(race, userTeam, unitsPerRace)
            const picked = userLobby.race === race
            const locked = !unlockedRaces.includes(race)
            return (
              <button
                key={race}
                className={`race-card${picked ? " picked" : ""}${locked ? " locked" : ""}`}
                onClick={() => onSelectRace(race)}
                // A locked card stays clickable while not ready: the click is what opens the shop.
                // The card itself says nothing about price (owner, 2026-09-06) — the shop does.
                disabled={userLobby.ready}
                title={locked ? t("lobby.lockedRaceHint", { context: pointerContext }) : undefined}
              >
                <img src={races[race]} alt={raceNames[race]} className="race-card-image" />
                <div className="race-card-body">
                  <p className="race-card-name">
                    {raceNames[race]}
                    {picked ? <span className="race-card-picked"> · {t("lobby.picked")}</span> : null}
                    {locked ? <span className="race-card-lock"> 🔒</span> : null}
                  </p>
                  {bonuses.map(([key, value]) => (
                    <p className={`race-card-bonus${value < 0 ? " negative" : ""}`} key={key}>
                      {describeBonus(key, value, userTeam)}
                    </p>
                  ))}
                  {specialUnit ? <p className="race-card-unlock">{t("lobby.unlocks", { unit: unitName(specialUnit) })}</p> : null}
                </div>
              </button>
            )
          })}
        </div>
      ) : (
        <p className="setup-empty p-small">
          {/* "Either side" stops being true the moment somebody has picked one. */}
          {survivalMode && peopleSide ? t("lobby.survivalTakeSlotHint") : t("lobby.takeSlotHint")}
        </p>
      )}
    </div>
  )

  /* READY and START GAME sit on the map's bottom-left corner (owner, 2026-09-29: "READY button in
     lobby not visible for small screens in the side bar"). At the foot of the class column they were
     the last thing in a panel that scrolls, below three portraits that grow to what the window has —
     so on a short window, and under the map on a tablet or a phone, the one button a player has
     left to press was off screen. On the map it is where the cells were just placed, whatever the
     shell, and the class column gives the cards its whole height. The status line rides with them:
     it is the answer to "why is START grey?", and it has to be read beside the button it explains. */
  const readyPanel = (
    <div className="lobby-ready-panel">
      <p className="setup-status">
        {blockers.length ? (
          <>
            {t("lobby.waitingOn")}{" "}
            {blockers.map((name, index) => (
              <span key={name}>
                {index > 0 ? ", " : ""}
                <b>{name}</b>
              </span>
            ))}
          </>
        ) : (
          t("lobby.everyoneReady")
        )}
        {emptySlots.length ? ` · ${emptySlots.join(" · ")}` : ""}
        {clashWarnings.length ? ` · ${clashWarnings.join(" · ")}` : ""}
        {willSpectate ? ` · ${t("lobby.willSpectate")}` : ""}
      </p>
      <div className="setup-actions">
        {/* Green while it is the move to make, quiet once it is the undo: READY and START GAME are
            the same act one player at a time, so an outlined READY beside a green START read as the
            lesser of the two rather than as the one thing the player has left to do. Disabled wins
            over both, so a READY nobody can press yet is still grey. */}
        <button
          className={userLobby.ready ? "quiet" : undefined}
          onClick={handleReady}
          disabled={!userCanClickReady}
          title={canAffordGame ? undefined : t("lobby.cannotAffordEntry", { credits: entryCost })}
        >
          {userLobby.ready ? t("lobby.notReady") : t("lobby.ready")}
        </button>
        <button id="playButton" onClick={handlePlay} disabled={!userCanStartGame}>
          {t("lobby.startGame")}
        </button>
      </div>
    </div>
  )

  const placedCells = userLobby.locations.length
  // Where only some starting cities are crowns (S and M), the hint says which: the first picks.
  const someAreCrowns = crowns < REQUIRED_CELLS
  const mapHint = !userTeam
    ? t("lobby.mapHintJoinSide")
    : placedCells < REQUIRED_CELLS
    ? someAreCrowns
      ? t("lobby.mapSize.hintPlace", { cities: REQUIRED_CELLS, count: crowns, context: pointerContext })
      : t("lobby.mapHintPlaceCells", { count: REQUIRED_CELLS, context: pointerContext })
    : someAreCrowns
    ? t("lobby.mapSize.hintAllPlaced", { placed: placedCells, total: REQUIRED_CELLS, count: crowns, context: pointerContext })
    : t("lobby.mapHintAllPlaced", { placed: placedCells, total: REQUIRED_CELLS, context: pointerContext })

  return (
    <BaseLayout topBar={topBar}>
      <div className="lobby-shell">
        <aside className="lobby-side">
          <div className="lobby-teams with-scroll-bar vertical">
            {/* Both sides are still doors until somebody walks through one, and which one they
                pick is the whole of survival's side choice — so it is said here rather than
                discovered by being refused on the far column. */}
            {survivalMode && !peopleSide ? (
              <p className="lobby-survival-hint p-small">{t("lobby.survivalFirstSeatPicks")}</p>
            ) : null}
            {["human", "zombie"].map((team) => (team === enemySide ? enemyBlock(team) : teamBlock(team)))}
          </div>
          <div className="lobby-chat">
            <Chat chatKey="lobby" messages={chatMessages} people={activeLobby.lobbyUsers} title={t("lobby.chatTitle")} placeholder={t("lobby.chatPlaceholder")} />
          </div>
        </aside>

        <main className="lobby-main">
          <div className="lobby-map">
            <Canvas
              key={mapSize}
              initialPosition={[
                Math.round((50 * activeWorld().columns) / 200),
                Math.round((50 * activeWorld().rows) / 100),
              ]}
              elements={elements}
              onClickHooks={[onClickLocation]}
              fitToParent
            />
            <p className="map-hint">{mapHint}</p>
            {readyPanel}
          </div>
          {setup}
        </main>
      </div>
    </BaseLayout>
  )
}

export default Lobby
